import { useEffect, useState } from "react";
import { FollowersStrip } from "./FollowersStrip";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus, X, Users, ArrowUpRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { FriendCompare } from "./FriendCompare";
import { Avatar } from "./Avatar";
import { PeopleFinder } from "./PeopleFinder";
import { FriendRequests } from "./FriendRequests";
import { JoinedCard } from "./JoinedCard";

export const FriendsSection = ({ me, geo }) => {
  const qc = useQueryClient();
  const { data: friends = [] } = useQuery({ queryKey: ["friends"], queryFn: api.friends });
  const [sel, setSel] = useState(null);
  const [link, setLink] = useState("");
  const current = friends.find((f) => f.friend_id === sel) || friends[0];

  useEffect(() => {
    if (window.location.hash === "#friends") document.getElementById("friends")?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const follow = async (e) => {
    e.preventDefault();
    if (!link.trim()) return;
    try {
      const f = await api.follow(link);
      toast.success(`You're now following ${f.name}`);
      setLink("");
      setSel(f.friend_id);
      qc.invalidateQueries({ queryKey: ["friends"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not follow");
    }
  };
  const navigate = useNavigate();
  const view = (id) => navigate(`/friend/${id}`);
  const unfollow = async (f) => {
    await api.unfollow(f.friend_id);
    toast.success(f.mutual ? `Removed ${f.name} from friends` : `Unfollowed ${f.name}`);
    if (sel === f.friend_id) setSel(null);
    ["friends", "people"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };

  return (
    <section id="friends" className="scroll-mt-24 px-4 sm:px-8 lg:px-12 py-20 border-t border-white/5" data-testid="friends-section">
      <p className="eyebrow !text-sky-300/80">Section 03</p>
      <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-2">Plan together</h2>
      <p className="text-slate-400 mt-3 max-w-xl text-sm sm:text-base">Add friends by their email or short name to see each other's maps, plans and dreams — or paste a share link to follow someone.</p>

      <FriendRequests />
      <JoinedCard me={me} />
      <PeopleFinder onView={view} />

      <div className="flex flex-col lg:flex-row gap-4 lg:items-center mt-8">
        <form onSubmit={follow} className="flex gap-2 w-full lg:w-[460px]">
          <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…/share/abc123" className="font-mono text-xs" data-testid="follow-link-input" />
          <Button type="submit" className="bg-sky-400 text-[#06121c] hover:bg-sky-300" data-testid="follow-submit-button"><UserPlus size={14} /> Follow</Button>
        </form>
        <FollowersStrip />
        <div className="flex flex-wrap gap-2" data-testid="friends-list">
          {friends.map((f) => (
            <div key={f.friend_id} className={`group flex items-center gap-2 pl-1 pr-2 py-1 rounded-full border transition-colors ${current?.friend_id === f.friend_id ? "border-sky-400/60 bg-sky-400/10" : "border-white/10 hover:border-white/25"}`}>
              <button onClick={() => setSel(f.friend_id)} className="flex items-center gap-2 text-sm" data-testid={`friend-chip-${f.friend_id}`}>
                <Avatar f={f} /> {f.name.split(" ")[0]}{f.mutual && <span className="text-[10px] text-emerald-300" data-testid={`friend-mutual-${f.friend_id}`}>· friend</span>}{!f.active && <span className="text-[10px] text-amber-400">· link changed</span>}
              </button>
              {f.active && <Link to={`/friend/${f.friend_id}`} className="text-slate-500 hover:text-sky-300" aria-label={`Open ${f.name}'s profile`} data-testid={`friend-profile-${f.friend_id}`}><ArrowUpRight size={13} /></Link>}
              <button onClick={() => unfollow(f)} className="text-slate-500 hover:text-red-400" aria-label={f.mutual ? `Remove ${f.name}` : `Unfollow ${f.name}`} data-testid={`friend-unfollow-${f.friend_id}`}><X size={13} /></button>
            </div>
          ))}
        </div>
      </div>

      {current ? (
        <FriendCompare key={current.friend_id} friend={current} me={me} geo={geo} />
      ) : (
        <div className="mt-8 rounded-2xl border border-dashed border-white/10 py-16 text-center text-slate-500 text-sm" data-testid="friends-empty">
          <Users className="mx-auto mb-3 opacity-60" /> No friends yet. Add someone by their email or short name above, or ask for their share link.
        </div>
      )}
    </section>
  );
};
